import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Portal,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState, type ElementType, type ReactNode } from 'react';
import { TbChevronUp, TbX } from 'react-icons/tb';
import { useRegisterBulkActionsShortcut } from '../contexts/BulkActionsShortcutContext';
import useDraggableFloatingBar from '../hooks/useDraggableFloatingBar';
import { Icon as YaxleIcon, type IconName } from '../ui';
import { FLOATING_ACTION_BAR_Z_INDEX } from '../utils/tableCopySx';

const PARTBAY_BLUE = '#0071E3';
/** ~3× the previous Actions-menu pill (~250–280px). */
const BAR_MAX_WIDTH = '800px';
/** Cap for primary bar buttons when overflowing into Other (used only at 5+ candidates). */
const MAX_PRIMARY_ACTIONS = 3;
/** Show all candidates as buttons up to this count; above it, keep top 3 and put the rest in Other. */
const MAX_ALL_PRIMARY_ACTIONS = MAX_PRIMARY_ACTIONS + 1;

export interface BulkActionBarItem {
    id: string;
    label: string;
    /** Yaxle glyph name; preferred over `icon`. */
    iconName?: IconName;
    icon?: ElementType;
    danger?: boolean;
    success?: boolean;
    /** Force this action into the Other menu instead of a primary bar button. */
    other?: boolean;
    /** Optional custom icon renderer (e.g. filled bookmark for flag/unflag). */
    renderIcon?: () => ReactNode;
}

export interface BulkActionBarProps {
    selectedCount: number;
    actions: BulkActionBarItem[];
    onClearSelection: () => void;
    onAction: (actionId: string) => void;
    isLoading?: boolean;
    /** When true, Cmd/Ctrl+K opens a dropdown of these same actions while selected. */
    shortcutEnabled?: boolean;
    /**
     * When `actions` is empty, show this message in the bar instead of the disabled
     * “Actions” button. Omit to keep the default disabled Actions + tooltip.
     */
    noActionsMessage?: string;
    /**
     * `absolute` (default): portal to the document and `position: fixed` so a
     * drag can pass page chrome (filters, Order Detail header, RHS panel).
     * Those sit outside the table's overflow box, which would clip an in-flow bar.
     * `sticky`: pin to the scrollport bottom while remaining in document flow.
     */
    position?: 'absolute' | 'sticky';
    /**
     * Distance from the bottom, in theme space units. Raise it on screens that
     * also float a save/discard bar, so the two stack instead of overlapping.
     */
    bottom?: number;
}

const menuItemColors = (action: BulkActionBarItem) => {
    const color = action.danger ? 'red.600' : action.success ? 'green.600' : 'gray.700';
    const hoverBg = action.danger ? 'red.50' : action.success ? 'green.50' : '#E6F1FD';
    const hoverColor = action.danger ? 'red.700' : action.success ? 'green.700' : PARTBAY_BLUE;
    const iconColor = action.danger ? 'red.500' : action.success ? 'green.500' : 'gray.500';
    return { color, hoverBg, hoverColor, iconColor };
};

const ActionIcon = ({ action, color }: { action: BulkActionBarItem; color?: string }) => {
    if (action.renderIcon) return <>{action.renderIcon()}</>;
    if (action.iconName) return <YaxleIcon name={action.iconName} size="md" color={color} />;
    if (!action.icon) return null;
    return <Icon as={action.icon} boxSize={4} color={color} />;
};

/**
 * Shared floating bulk-actions pill used by Dashboard, Inventory, and Purchase Orders.
 * Up to 4 actions render as one-click buttons; at 5+, top 3 stay primary and the rest go under Other.
 * Actions marked `other` always land in the Other menu.
 */
const BulkActionBar = ({
    selectedCount,
    actions,
    onClearSelection,
    onAction,
    isLoading = false,
    shortcutEnabled = false,
    noActionsMessage,
    position = 'absolute',
    bottom = 4,
}: BulkActionBarProps) => {
    const [pendingActionId, setPendingActionId] = useState<string | null>(null);
    const { dragHandleProps } = useDraggableFloatingBar(selectedCount > 0);

    useEffect(() => {
        if (!isLoading) setPendingActionId(null);
    }, [isLoading]);

    const handleAction = useCallback(
        (actionId: string) => {
            setPendingActionId(actionId);
            onAction(actionId);
        },
        [onAction],
    );

    useRegisterBulkActionsShortcut(
        shortcutEnabled && selectedCount > 0
            ? { selectedCount, actions, onAction: handleAction, isLoading }
            : null,
    );

    if (selectedCount <= 0) return null;

    const forcedOther = actions.filter((a) => a.other);
    const candidates = actions.filter((a) => !a.other);
    // ≤4 → all primary (Other would only hold 1). 5+ → top 3 + Other (≥2).
    const shouldOverflow = candidates.length > MAX_ALL_PRIMARY_ACTIONS;
    const primaryActions = shouldOverflow
        ? candidates.slice(0, MAX_PRIMARY_ACTIONS)
        : candidates;
    const otherActions = shouldOverflow
        ? [...candidates.slice(MAX_PRIMARY_ACTIONS), ...forcedOther]
        : [...forcedOther];
    const hasNoActions = actions.length === 0;
    const otherIsPending =
        isLoading &&
        !!pendingActionId &&
        otherActions.some((action) => action.id === pendingActionId);

    const barButtonProps = {
        size: 'sm' as const,
        h: '32px',
        px: 3,
        variant: 'outline' as const,
        borderColor: 'whiteAlpha.500',
        color: 'white',
        bg: 'whiteAlpha.200',
        _hover: { bg: 'whiteAlpha.300' },
        _active: { bg: 'whiteAlpha.400' },
        borderRadius: 'full',
        fontWeight: 'medium' as const,
        fontSize: 'sm',
        isDisabled: isLoading,
    };

    const isPortaled = position === 'absolute';
    const bar = (
        <Flex
            position={isPortaled ? 'fixed' : position}
            bottom={bottom}
            left={0}
            right={0}
            justify="center"
            px={4}
            mt={position === 'sticky' ? 4 : undefined}
            zIndex={FLOATING_ACTION_BAR_Z_INDEX}
            pointerEvents="none"
        >
            <HStack
                {...dragHandleProps}
                pointerEvents="auto"
                bg={PARTBAY_BLUE}
                color="white"
                borderRadius="full"
                pl={3}
                pr={2}
                py={1.5}
                spacing={2}
                boxShadow="none"
                minH="44px"
                w="fit-content"
                maxW={`min(100%, ${BAR_MAX_WIDTH})`}
                overflow="hidden"
            >
                <Tooltip label="Drag to move" hasArrow placement="top" openDelay={500}>
                    <Box display="inline-flex" flexShrink={0} aria-hidden>
                        <YaxleIcon name="gripVertical" size="sm" color="whiteAlpha.700" />
                    </Box>
                </Tooltip>

                <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap" flexShrink={0} mr={1}>
                    {selectedCount} selected
                </Text>

                <HStack spacing={1.5} flex="1" minW={0} overflow="hidden">
                    {hasNoActions ? (
                        noActionsMessage ? (
                            <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color="whiteAlpha.700"
                                whiteSpace="normal"
                                lineHeight="short"
                                py={0.5}
                            >
                                {noActionsMessage}
                            </Text>
                        ) : (
                            <Tooltip label="No available actions" hasArrow placement="top">
                                <Box as="span" display="inline-flex">
                                    <Button {...barButtonProps} isDisabled>
                                        Actions
                                    </Button>
                                </Box>
                            </Tooltip>
                        )
                    ) : (
                        <>
                            {primaryActions.map((action) => (
                                <Button
                                    key={action.id}
                                    {...barButtonProps}
                                    flexShrink={0}
                                    leftIcon={
                                        action.renderIcon || action.icon || action.iconName ? (
                                            <Box
                                                as="span"
                                                display="inline-flex"
                                                color={
                                                    action.danger
                                                        ? 'red.200'
                                                        : action.success
                                                          ? 'green.200'
                                                          : 'currentColor'
                                                }
                                            >
                                                <ActionIcon action={action} />
                                            </Box>
                                        ) : undefined
                                    }
                                    isLoading={isLoading && pendingActionId === action.id}
                                    onClick={() => handleAction(action.id)}
                                >
                                    {action.label}
                                </Button>
                            ))}

                            {otherActions.length > 0 && (
                                <Menu placement="top">
                                    <MenuButton
                                        as={Button}
                                        {...barButtonProps}
                                        flexShrink={0}
                                        rightIcon={
                                            <Icon as={TbChevronUp as ElementType} boxSize={3.5} />
                                        }
                                        isLoading={otherIsPending}
                                    >
                                        Other
                                    </MenuButton>
                                    <MenuList minW="200px" py={1}>
                                        {otherActions.map((action) => {
                                            const { color, hoverBg, hoverColor, iconColor } =
                                                menuItemColors(action);
                                            return (
                                                <MenuItem
                                                    key={action.id}
                                                    fontSize="sm"
                                                    px={3}
                                                    py={2}
                                                    color={color}
                                                    _hover={{
                                                        bg: hoverBg,
                                                        color: hoverColor,
                                                    }}
                                                    onClick={() => handleAction(action.id)}
                                                >
                                                    <HStack spacing={2}>
                                                        <ActionIcon action={action} color={iconColor} />
                                                        <Text>{action.label}</Text>
                                                    </HStack>
                                                </MenuItem>
                                            );
                                        })}
                                    </MenuList>
                                </Menu>
                            )}
                        </>
                    )}
                </HStack>

                <IconButton
                    aria-label="Clear selection"
                    icon={<Icon as={TbX as React.ElementType} boxSize={4} />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    borderRadius="full"
                    minW="32px"
                    h="32px"
                    flexShrink={0}
                    _hover={{ bg: 'whiteAlpha.200' }}
                    onClick={onClearSelection}
                    isDisabled={isLoading}
                />
            </HStack>
        </Flex>
    );

    return isPortaled ? <Portal>{bar}</Portal> : bar;
};

export default BulkActionBar;
